import React, { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Box,
  Link,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  LinkedIn,
  Facebook,
  Email,
  Phone,
  Menu as MenuIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import Grid from '@mui/material/GridLegacy';
import { trackPhoneClick } from '../utils/gtag';

// Import logo
const logo = require('../assets/logo_transparent.png');
const logoWhite = require('../assets/logo_white_1.png');
const IPA = require('../assets/IPA_2.png');
const register = require('../assets/Register.png');

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const menuItems = [
    { text: 'Home', path: '/' },
    { text: 'Services', path: '/services' },
    { text: 'Tools', path: '/tools' },
    { text: 'About', path: '/about' },
    { text: 'Contact', path: '/contact' },
  ];

  const handleMenuClick = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMenuItemClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar 
        position="sticky"
        sx={{ 
          backgroundColor: 'background.paper',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          borderRadius: 0,
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
            <Box
              component={RouterLink}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                color: 'primary.main',
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="GS Accountants Logo"
                sx={{
                  height: '50px',
                  width: 'auto',
                  display: 'block',
                }}
              />
            </Box>

            {isMobile ? (
              <>
                <IconButton
                  edge="end"
                  color="primary"
                  aria-label="menu"
                  onClick={handleMenuClick}
                >
                  <MenuIcon />
                </IconButton>
                <Drawer
                  anchor="right"
                  open={mobileMenuOpen}
                  onClose={handleMenuClick}
                  PaperProps={{
                    sx: {
                      width: '100%',
                      maxWidth: '300px',
                      backgroundColor: 'background.paper',
                      p: 2,
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <IconButton onClick={handleMenuClick} color="primary">
                      <CloseIcon />
                    </IconButton>
                  </Box>
                  <List>
                    {menuItems.map((item) => (
                      <ListItem
                        key={item.path}
                        component={RouterLink}
                        to={item.path}
                        onClick={handleMenuItemClick}
                        sx={{
                          color: 'primary.main',
                          borderBottom: location.pathname === item.path ? '2px solid' : 'none',
                          borderColor: 'secondary.main',
                          '&:hover': {
                            backgroundColor: 'rgba(0,0,0,0.04)',
                          },
                        }}
                      >
                        <ListItemText 
                          primary={item.text}
                          primaryTypographyProps={{
                            sx: { fontWeight: location.pathname === item.path ? 600 : 400 }
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Drawer>
              </>
            ) : (
              <Box sx={{ display: 'flex', gap: 2 }}>
                {menuItems.map((item) => (
                  <Button 
                    key={item.path}
                    color="primary" 
                    component={RouterLink} 
                    to={item.path}
                    sx={{
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: -1,
                        left: 0,
                        width: '100%',
                        height: '2px',
                        backgroundColor: 'secondary.main',
                        transform: location.pathname === item.path ? 'scaleX(1)' : 'scaleX(0)',
                        transition: 'transform 0.2s ease-in-out',
                      }
                    }}
                  >
                    {item.text}
                  </Button>
                ))}
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Box component="main" sx={{ flexGrow: 1 }}>
        {children}
      </Box>

      <Box 
        component="footer" 
        sx={{ 
          py: 8, 
          px: 6, 
          mt: 'auto', 
          backgroundColor: 'primary.main',
          color: 'white',
          borderTop: '4px solid',
          borderColor: 'secondary.main',
        }}
      >
        <Grid container spacing={6} justifyContent="space-between">
          {/* Company Info */}
          <Grid item xs={12} md={4}>
            <Box sx={{ mb: 3 }}>
              <Box
                component="img"
                src={logoWhite}
                alt="GS Accountants Logo"
                sx={{
                  height: '50px',
                  width: 'auto',
                  display: 'block',
                  filter: 'brightness(0) invert(1)',
                  opacity: 1,
                  mb: 2,
                }}
              />
              <Typography variant="body1" sx={{ mb: 2, opacity: 0.9 }}>
                Professional accounting services you can trust.
                <br />
                Serving Sydney businesses with expertise and dedication.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton 
                  component="a" 
                  href="https://www.linkedin.com/in/nancy-michail-66756b95/" 
                  target="_blank"
                  sx={{ color: 'white', '&:hover': { color: 'secondary.main' } }}
                >
                  <LinkedIn />
                </IconButton>
                <IconButton 
                  component="a" 
                  href="https://www.facebook.com/share/1HTYsToMVw/?mibextid=wwXIfr" 
                  target="_blank"
                  sx={{ color: 'white', '&:hover': { color: 'secondary.main' } }}
                >
                  <Facebook />
                </IconButton>
              </Box>
            </Box>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Quick Links
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Link 
                component={RouterLink} 
                to="/" 
                color="inherit" 
                sx={{ 
                  opacity: 0.9, 
                  '&:hover': { 
                    opacity: 1,
                    color: 'secondary.main',
                  },
                  textDecoration: 'none',
                }}
              >
                Home
              </Link>
              <Link 
                component={RouterLink} 
                to="/services" 
                color="inherit" 
                sx={{ 
                  opacity: 0.9, 
                  '&:hover': { 
                    opacity: 1,
                    color: 'secondary.main',
                  },
                  textDecoration: 'none',
                }}
              >
                Services
              </Link>
              <Link 
                component={RouterLink} 
                to="/tools" 
                color="inherit" 
                sx={{ 
                  opacity: 0.9, 
                  '&:hover': { 
                    opacity: 1,
                    color: 'secondary.main',
                  },
                  textDecoration: 'none',
                }}
              >
                Financial Tools
              </Link>
              <Link 
                component={RouterLink} 
                to="/about" 
                color="inherit" 
                sx={{ 
                  opacity: 0.9, 
                  '&:hover': { 
                    opacity: 1,
                    color: 'secondary.main',
                  },
                  textDecoration: 'none',
                }}
              >
                About Us
              </Link>
              <Link 
                component={RouterLink} 
                to="/contact" 
                color="inherit" 
                sx={{ 
                  opacity: 0.9, 
                  '&:hover': { 
                    opacity: 1,
                    color: 'secondary.main',
                  },
                  textDecoration: 'none',
                }}
              >
                Contact
              </Link>
            </Box>
          </Grid>

          {/* Contact & Accreditations */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Contact Us
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Email sx={{ fontSize: 20, opacity: 0.9 }} />
                <Link 
                  href="mailto:nancy@gsaccountants.com.au" 
                  color="inherit"
                  sx={{ 
                    opacity: 0.9, 
                    '&:hover': { 
                      opacity: 1,
                      color: 'secondary.main',
                    },
                    textDecoration: 'none',
                  }}
                >
                  nancy@gsaccountants.com.au
                </Link>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Phone sx={{ fontSize: 20, opacity: 0.9 }} />
                <Link
                  href="tel:+61425660773"
                  onClick={trackPhoneClick}
                  color="inherit"
                  sx={{ 
                    opacity: 0.9, 
                    '&:hover': { 
                      opacity: 1,
                      color: 'secondary.main',
                    },
                    textDecoration: 'none',
                  }}
                >
                  (+61) 0425 660 773
                </Link>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                Turvey St, Padstow NSW
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                ABN 95 622 972 445
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box
                  component="img"
                  src={register}
                  sx={{
                    height: '120px',
                    width: 'auto',
                  }}
                />
              <Box
                component="img"
                src={IPA}
                sx={{
                  height: '120px',
                  width: 'auto',
                }}
              />
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ mt: 6, pt: 3, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <Typography variant="body2" align="center" sx={{ opacity: 0.9 }}>
            © {new Date().getFullYear()} GS Accountants. All rights reserved.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, gap: 3 }}>
            <Link 
              component={RouterLink} 
              to="/privacy-policy" 
              color="inherit" 
              sx={{ 
                opacity: 0.9, 
                '&:hover': { 
                  opacity: 1,
                  color: 'secondary.main',
                },
                textDecoration: 'none',
                fontSize: '0.875rem',
              }}
            >
              Privacy Policy
            </Link>
            <Link 
              component={RouterLink} 
              to="/terms-of-service" 
              color="inherit" 
              sx={{ 
                opacity: 0.9, 
                '&:hover': { 
                  opacity: 1,
                  color: 'secondary.main',
                },
                textDecoration: 'none',
                fontSize: '0.875rem',
              }}
            >
              Terms of Service
            </Link>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Layout; 